/**
 * Formats a time delta (in milliseconds) into a human-readable string
 *
 * Format rules:
 * - < 1min: "30s"
 * - < 1h: "15min"
 * - < 1d: "3h 05" (hours and minutes)
 * - < 1y: "5d" (days only)
 * - >= 1y: "2y" (years only)
 *
 * @param milliseconds - Time delta in milliseconds
 * @returns Formatted time string
 */
export function timeSince(milliseconds: number): string {
  if (milliseconds < 0) {
    return '0s';
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const years = Math.floor(days / 365);

  // Less than 1 minute
  if (minutes < 1) {
    return `${seconds}s`;
  }

  // Less than 1 hour
  if (hours < 1) {
    return `${minutes}min`;
  }

  // Less than 1 day - show hours and minutes
  if (days < 1) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes.toString().padStart(2, '0')}`;
  }

  // Less than 1 year - show days only
  if (years < 1) {
    return `${days}d`;
  }

  // 1 year or more - show years only
  return `${years}y`;
}

/**
 * Formats a Date or timestamp into time since now
 *
 * @param date - Date object or timestamp
 * @returns Formatted time string
 */
export function timeSinceDate(date: Date | number): string {
  const timestamp = typeof date === 'number' ? date : date.getTime();
  const now = Date.now();
  const delta = now - timestamp;

  return timeSince(delta);
}

/**
 * Formats a Date or timestamp into time until that date
 *
 * @param date - Date object or timestamp
 * @returns Formatted time string (negative delta handled as 0)
 */
export function timeUntilDate(date: Date | number): string {
  const timestamp = typeof date === 'number' ? date : date.getTime();
  const now = Date.now();
  const delta = timestamp - now;

  return timeSince(Math.max(0, delta));
}

/**
 * Formats a time delta (ms) into natural language for UI display.
 * < 45s       → "just now"
 * 45s–<2min   → "1 minute ago"
 * 2–59min     → "X minutes ago"
 * 60–119min   → "1 hour ago"
 * 2–23h       → "X hours ago"
 * 24–47h      → "yesterday"
 * 48h+        → "X days ago"
 */
export function timeSinceHuman(milliseconds: number): string {
  const ms = Math.max(0, milliseconds);
  if (ms < 45_000) return 'just now';

  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 2) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  if (hours < 2) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  if (days < 2) return 'yesterday';
  return `${days} days ago`;
}
